/**
 * Работа с датой в формате `ДД.ММ.ГГГГ`.
 *
 * Формат выбран не нами: в этом виде даты лежат в контрактах отчёта и в
 * этом же виде их читает человек. Значит контрол обязан принимать и отдавать
 * именно строку, а `Date` держать внутри — иначе каждый вызывающий заводил
 * бы свой парсер, и они бы разъехались.
 *
 * Внешних библиотек здесь нет намеренно. Нужен ровно месяц-сетка и разбор
 * десяти символов; ради этого тащить в дизайн-систему пакет с локалями
 * (который потом надо объявлять и в `ui-storybook`, и в `dedupe`) — дорого.
 *
 * @format
 */

/** Дни недели с понедельника — русская неделя начинается с него. */
export const WEEKDAYS = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс'];

/** Месяцы в именительном падеже — заголовок календаря, а не дата в тексте. */
export const MONTHS_NOMINATIVE = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь',
];

/**
 * `07.07.2026` → `Date`. Непригодный вход даёт `null`: контрол показывает
 * то, что ввели, а не выдуманную дату.
 */
export function parseDate(value: string | undefined): Date | null {
  if (!value) return null;
  const match = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(value.trim());
  if (!match) return null;
  const [, day, month, year] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  // Календарь молча переносит 31 февраля на март — сверяем, что вернулось
  // то же число, которое просили.
  if (date.getDate() !== Number(day) || date.getMonth() !== Number(month) - 1) return null;
  return date;
}

/** `Date` → `07.07.2026`. */
export function formatDate(date: Date): string {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${day}.${month}.${date.getFullYear()}`;
}

/** Один и тот же день, без учёта времени. */
export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getDate() === b.getDate() &&
    a.getMonth() === b.getMonth() &&
    a.getFullYear() === b.getFullYear()
  );
}

/**
 * Сетка месяца: шесть недель по семь дней, начиная с понедельника.
 *
 * Высота фиксирована намеренно — месяц, укладывающийся в пять недель, не
 * должен схлопывать поповер, иначе при переключении месяцев календарь
 * прыгает под курсором.
 */
export function monthGrid(month: Date): Date[] {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  // getDay(): 0 — воскресенье. Смещаем к понедельнику.
  const lead = (first.getDay() + 6) % 7;
  const start = new Date(first.getFullYear(), first.getMonth(), 1 - lead);
  return Array.from({length: 42}, (_, index) => {
    const day = new Date(start);
    day.setDate(start.getDate() + index);
    return day;
  });
}

/** Сдвиг месяца — вперёд или назад, без переполнения по числу. */
export function shiftMonth(month: Date, delta: number): Date {
  return new Date(month.getFullYear(), month.getMonth() + delta, 1);
}

/** Дата вне разрешённых границ. Границы включительные. */
export function isOutOfRange(date: Date, min: Date | null, max: Date | null): boolean {
  if (min && date < new Date(min.getFullYear(), min.getMonth(), min.getDate())) return true;
  if (max && date > new Date(max.getFullYear(), max.getMonth(), max.getDate())) return true;
  return false;
}
